import type { Logger } from "@expert/logging";

import ky from "ky";

import type { DefaultPayload } from "./types";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

type ActivateDevicePayload = DefaultPayload & {
    manualDeviceInfo?: {
        imei: string;
        iccid: string;
    };
};

// AT&T wraps the real activation-blocking reason in generic codes. These descriptions carry no
// actionable information for the expert, so they are dropped when surfacing the failure reason.
const GENERIC_ATT_ACTIVATION_ERRORS = new Set([
    "Request performed out of sequence with business process",
    "Data Error",
]);

/** Pick the actionable AT&T reason(s) out of the raw `details`, dropping generic wrapper text. */
const pickActivationFailureReason = (details?: string[]): string | undefined => {
    const meaningful = (details ?? [])
        .map((detail) => detail.trim())
        .filter((detail) => detail.length > 0 && !GENERIC_ATT_ACTIVATION_ERRORS.has(detail));
    return meaningful.length > 0 ? meaningful.join(" ") : undefined;
};

export const activateDevice = async (
    ssoAccessToken: string,
    payload: ActivateDevicePayload,
    verifiedCustomerTokenOrExtraAuthToken: string,
    logger: Logger,
    apiBaseUrl: string,
) => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/activate`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: { ...payload, token: verifiedCustomerTokenOrExtraAuthToken },
            timeout: defaultTimeoutInMS,
        });

        const { result, details } = await resp.json<{ result: string; reason?: string; details?: string[] }>();
        if (result === "SUCCESS") {
            return { wasSuccessful: true, failureReason: undefined };
        }
        // A 200 with a non-SUCCESS result is an AT&T business rejection (e.g. account lock). Surface
        // the actionable reason so the expert can act instead of blindly retrying an un-retryable error.
        return { wasSuccessful: false, failureReason: pickActivationFailureReason(details) };
    } catch (error) {
        logger.warn({ error }, `Error: Could not activate device.`);
        return { wasSuccessful: false, failureReason: undefined };
    }
};
